import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { createLogger } from "../../../../lib/logger";
import { Deployment } from "../../../../types";

const log = createLogger("useDeploymentLogs");

export interface UseDeploymentLogsResult {
  logs: Record<string, string[]>;
  watchingDeployment: string | null;
  viewLogs: (deploymentId: string) => Promise<void>;
  stopWatching: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8003/api";

export function useDeploymentLogs(): UseDeploymentLogsResult {
  const [logs, setLogs] = useState<Record<string, string[]>>({});
  const [watchingDeployment, setWatchingDeployment] = useState<string | null>(
    null
  );
  const socketRef = useRef<Socket | null>(null);

  // Initialize socket connection
  useEffect(() => {
    const socket: Socket = io(API_BASE, {
      transports: ["websocket"],
      autoConnect: false,
    });

    socketRef.current = socket;

    // Listen for deployment updates
    socket.on("deploymentUpdated", (updatedDeployment: Deployment) => {
      // This could be handled by the deployments hook, but keeping here for socket management
      log.debug("Deployment updated", updatedDeployment.id);
    });

    // Listen for deployment logs
    socket.on(
      "deploymentLog",
      (data: { deploymentId: string; log: string }) => {
        setLogs((prev) => ({
          ...prev,
          [data.deploymentId]: [...(prev[data.deploymentId] || []), data.log],
        }));
      }
    );

    // Connect socket
    socket.connect();

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  const viewLogs = useCallback(
    async (deploymentId: string) => {
      try {
        // Toggle the watched deployment
        if (watchingDeployment === deploymentId) {
          setWatchingDeployment(null);
          return;
        }

        setWatchingDeployment(deploymentId);

        // Join the deployment room for real-time logs
        socketRef.current?.emit("joinDeployment", deploymentId);

        // Fetch existing logs
        const response = await fetch(
          `${API_BASE}/deployments/${deploymentId}/logs`
        );
        if (response.ok) {
          const data = await response.json();
          setLogs((prev) => ({
            ...prev,
            [deploymentId]: data.logs || [],
          }));
        }
      } catch (error) {
        log.error("Failed to fetch logs", error);
      }
    },
    [watchingDeployment]
  );

  const stopWatching = useCallback(() => {
    setWatchingDeployment(null);
  }, []);

  return {
    logs,
    watchingDeployment,
    viewLogs,
    stopWatching,
  };
}
